import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { submitInquiry } from '@/lib/auth/session';

/*
  파트너 페이지 「문의하기」(DOC-96 §9). 적어서 보내면 외주구매팀 메일로 간다 — 사람이 「보내기」 를
  누른 발송이고, 회신은 적은 이메일로 온다. 표에 쌓지 않고 화면도 없다.

  성공·실패 문장은 각각 하나다.
*/
export function InquiryForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSubmit = !pending && name.trim() !== '' && email.trim() !== '' && message.trim() !== '';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setFailed(false);
    try {
      await submitInquiry({
        name: name.trim(),
        company: blankToNull(company),
        phone: blankToNull(phone),
        email: email.trim(),
        message: message.trim(),
      });
      onDone();
    }
    catch {
      setFailed(true);
    }
    finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <Field label="이름" required>
        <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
      </Field>
      <Field label="업체">
        <input className="input-field" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
      </Field>
      <Field label="전화">
        <input className="input-field" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
      </Field>
      <Field label="이메일" required>
        <input
          className="input-field"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputMode="email"
          autoComplete="email"
          required
        />
      </Field>
      <Field label="내용" required>
        <textarea className="input-field" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required />
      </Field>

      {failed && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>
          보내지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      )}

      <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
        {pending ? '보내는 중입니다…' : '보내기'}
      </button>
    </form>
  );
}

function Field({ label, required = false, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
        {label}
        {required && <span style={{ color: 'var(--danger)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
